/**
 * Environment Configuration
 *
 * Loads and validates environment variables from .env files
 * Provides type-safe access to configuration values
 */

/**
 * Validates that a required environment variable exists
 * @throws Error if variable is missing
 */
function getRequiredEnv(key: string, allowEmptyInDev = false): string {
  const value = import.meta.env[key];

  // In development mode, allow certain vars to be empty with a warning
  if (!value && allowEmptyInDev && import.meta.env.DEV) {
    console.warn(
      `Warning: ${key} is not set. Using placeholder value.\n` +
        `Please add ${key} to your .env.local file.`
    );
    return '';
  }

  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

/**
 * Gets an optional environment variable with a default value
 */
function getOptionalEnv(key: string, defaultValue: string): string {
  return import.meta.env[key] || defaultValue;
}

/**
 * Application environment configuration
 */
export const env = {
  /**
   * Backend API base URL
   * @example "https://api.example.com" or "http://127.0.0.1:8000"
   */
  apiBaseUrl: getRequiredEnv('VITE_API_BASE_URL'),

  /**
   * Whether analytics are enabled
   * @default false
   */
  enableAnalytics: getOptionalEnv('VITE_ENABLE_ANALYTICS', 'false') === 'true',

  /**
   * Current environment
   * @default "development"
   */
  environment: getOptionalEnv('VITE_ENVIRONMENT', 'development') as
    | 'development'
    | 'staging'
    | 'production',

  /**
   * Whether app is in development mode
   */
  isDevelopment: import.meta.env.DEV,

  /**
   * Whether app is in production mode
   */
  isProduction: import.meta.env.PROD,

  /**
   * Anonymous donor ID for guest checkouts
   */
  anonymousDonorId: parseInt(getOptionalEnv('VITE_ANONYMOUS_DONORID', '10001'), 10),

  /**
   * Online payment currency
   * @default "usd"
   */
  onlineCurrency: getOptionalEnv('VITE_ONLINE_CURRENCY', 'usd'),

  /**
   * Online payment locale
   * @default "en-US"
   */
  onlineLocale: getOptionalEnv('VITE_ONLINE_LOCALE', 'en-US'),

  /**
   * Online payment fee percentage (as decimal)
   * @default 0.035 (3.5%)
   */
  onlineFeePercentage: parseFloat(getOptionalEnv('VITE_ONLINE_FEE_PERCENTAGE', '0.035')),

  /**
   * Online payment fixed fee amount in cents
   * @default 50
   */
  onlineFeeFixedAmountCents: parseInt(
    getOptionalEnv('VITE_ONLINE_FEE_FIXED_AMOUNT_CENTS', '50'),
    10
  ),

  /**
   * Online payment fee message shown to users
   */
  onlineFeeMessage: getOptionalEnv(
    'VITE_ONLINE_FEE_MESSAGE',
    'I wish to contribute 3.5% + 50 cents towards credit card processing fees charged by the credit card provider.'
  ),

  /**
   * Donation type ID to use for processing fees
   * @default 99999
   */
  onlineFeeDonationTypeId: parseInt(getOptionalEnv('VITE_ONLINE_FEE_DONATION_TYPE', '99999'), 10),

  /**
   * Description for processing fee donation type
   */
  onlineFeeDonationTypeDescription: getOptionalEnv(
    'VITE_ONLINE_FEE_DONATION_TYPE_DESCRIPTION',
    'Processing Fee Contribution'
  ),

  /**
   * Enable anonymous donation option
   * @default false
   */
  onlineEnableAnonymous: getOptionalEnv('VITE_ONLINE_ENABLE_ANONYMOUS', 'false') === 'true',

  /**
   * Auth0 Configuration
   */
  auth0: {
    /**
     * Auth0 tenant domain
     * @example "masjidconnect.prod-us-1.auth0.com"
     */
    domain: getOptionalEnv('VITE_AUTH0_DOMAIN', ''),

    /**
     * Auth0 application client ID
     */
    clientId: getOptionalEnv('VITE_AUTH0_CLIENT_ID', ''),

    /**
     * Redirect URI after Auth0 login
     * @example "http://localhost:5173/callback"
     */
    redirectUri: `${window.location.origin}/callback`,

    /**
     * Auth0 Management API audience/identifier
     */
    audience: getOptionalEnv('VITE_AUTH0_AUDIENCE', ''),

    /**
     * Whether Auth0 is enabled
     */
    enabled: getOptionalEnv('VITE_AUTH0_ENABLED', 'false') === 'true',
  },
} as const;

/**
 * Validates all required environment variables on app startup
 * @throws Error if any required variables are missing
 */
export function validateEnv(): void {
  const required = ['VITE_API_BASE_URL'];

  const missing = required.filter((key) => !import.meta.env[key]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables:\n${missing.map((k) => `  - ${k}`).join('\n')}\n\n` +
        'Please check your .env.local file or environment configuration.'
    );
  }
}

// Validate on module load in production
if (import.meta.env.PROD) {
  validateEnv();
}
